import { useCallback } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMergeCandidates, fetchMergePreview, fetchMergeStates, MergeState } from "@/common/lib/api/visitMerges";

export const mergeStateQueryKey = (ids: string[]) => ["merge-state", [...ids].sort()] as const;

/**
 * Slow to every 5 minutes while the endpoint 404s (data === null; web ships before the API), so a passing
 * 404 (a routing blip, an old pod during a rollout) does not leave the merge UI dormant for good.
 */
export const mergeStateRefetchInterval = (query: { state: { data: unknown } }) =>
    query.state.data === null ? 300_000 : 60_000;

const NO_STATES: Record<string, MergeState> = {};

/**
 * `available` is false while loading, when the endpoint 404s (feature not deployed), and when it has
 * never answered successfully; the UI renders nothing then. A failed refetch keeps the last good data.
 * `updatedAt` is when the states were fetched (0 before any answer).
 * `keepWhileLoading` serves the previous id set's states while a changed set loads, so a list gaining
 * or losing a row keeps its pills (and an open popover) instead of blanking them; ids new to the set
 * read as no state until the answer arrives. While it serves them, `available` is true and `updatedAt` is 0;
 * if the changed set's request fails, they are dropped.
 */
export function useMergeStates(
    ids: string[],
    { keepWhileLoading = false }: { keepWhileLoading?: boolean } = {},
): {
    states: Record<string, MergeState>;
    available: boolean;
    updatedAt: number;
} {
    const { data, dataUpdatedAt } = useQuery({
        queryKey: mergeStateQueryKey(ids),
        queryFn: () => fetchMergeStates(ids),
        enabled: ids.length > 0,
        staleTime: 30_000,
        refetchInterval: mergeStateRefetchInterval,
        // The minute poll is the retry: one more attempt, not the app-wide three, per failed fetch.
        retry: 1,
        placeholderData: keepWhileLoading ? keepPreviousData : undefined,
    });
    return { states: data ?? NO_STATES, available: data !== null && data !== undefined, updatedAt: dataUpdatedAt };
}

// The dialog builds a merge from these, so each open loads them live: an entry is dropped as soon as no
// dialog uses it, rather than served from cache (the app default keeps data fresh for 2 minutes).
export function useMergeCandidates(visitId: string | null, q: string) {
    return useQuery({
        queryKey: ["merge-candidates", visitId, q] as const,
        queryFn: () => fetchMergeCandidates(visitId!, q || null),
        enabled: !!visitId,
        refetchInterval: 10_000,
        placeholderData: (prev) => prev,
        gcTime: 0,
    });
}

/** refetchInterval: polls while the dialog can only check the picked visit by id (it left the candidates list). */
export function useMergePreview(
    visitId: string | null,
    otherId: string | null,
    refetchInterval: number | false = false,
) {
    return useQuery({
        queryKey: ["merge-preview", visitId, otherId] as const,
        queryFn: () => fetchMergePreview(visitId!, otherId!),
        enabled: !!visitId && !!otherId,
        gcTime: 0,
        refetchInterval,
    });
}

export function useInvalidateMergeState() {
    const qc = useQueryClient();
    return useCallback(() => {
        qc.invalidateQueries({ queryKey: ["merge-state"] });
        qc.invalidateQueries({ queryKey: ["merge-candidates"] });
        qc.invalidateQueries({ queryKey: ["merge-preview"] });
        // A merge adds a visit to the Recycle Bin and an unmerge takes it out.
        qc.invalidateQueries({ queryKey: ["recycle-bin"] });
    }, [qc]);
}
